// src/components/LanguageSelector.js
import React, { useState, useEffect } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'zh-TW', name: '繁體中文'},
  { code: 'ja', name: '日本語'},
  { code: 'en', name: 'English'},
  { code: 'zh-CN', name: '简体中文'}  
];

const LanguageSelector = ({ variant = 'button' }) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const open = Boolean(anchorEl);

  // Check if i18n is ready
  useEffect(() => {
    if (i18n && typeof i18n.changeLanguage === 'function') {
      setIsReady(true);
    }
  }, [i18n]);

  // Get current language, handling potential regional codes
  const getCurrentLanguage = () => {
    // Add null check for i18n and i18n.language
    const currentCode = (i18n && i18n.language) || 'zh-TW';
    
    // Check for exact match first
    let lang = languages.find(lang => lang.code === currentCode);
    
    // If no exact match, try to match by language family
    if (!lang && currentCode && currentCode.includes('-')) {
      const baseCode = currentCode.split('-')[0];
      lang = languages.find(lang => lang.code.startsWith(baseCode));
    }
    
    return lang || languages[0];
  };

  const currentLanguage = getCurrentLanguage();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    // Check if changeLanguage is available before calling
    if (i18n && typeof i18n.changeLanguage === 'function') {
      i18n.changeLanguage(langCode).then(() => {
        localStorage.setItem('preferredLanguage', langCode);
        handleClose();
      }).catch((error) => {
        console.error('Error changing language:', error);
        handleClose();
      });
    } else {
      console.error('i18n.changeLanguage is not available');
      // Fallback: just save to localStorage and reload
      localStorage.setItem('preferredLanguage', langCode);
      window.location.reload();
    }
  };

  // Don't render until i18n is ready
  if (!isReady) {
    return null;
  }

  if (variant === 'icon' || isMobile) {
    return (
      <>
        <IconButton
          onClick={handleClick}
          color="inherit"
          sx={{ 
            ml: 1,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 1,
            padding: '6px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.5)',
            }
          }}
        >
          <LanguageIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 180,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1,
              }
            }
          }}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              selected={lang.code === currentLanguage.code}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography sx={{ mr: 1, fontSize: '1.2rem' }}>
                  {lang.flag}
                </Typography>
                <Typography>
                  {lang.name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={<LanguageIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          color: 'inherit',
          textTransform: 'none',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: 1,
          px: 2,
          py: 0.75,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ mr: 1, fontSize: '1.2rem' }}>
            {currentLanguage.flag}
          </Typography>
          <Typography>
            {currentLanguage.name}
          </Typography>
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 200,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
            }
          }
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={lang.code === currentLanguage.code}
            sx={{
              '&.Mui-selected': {
                backgroundColor: theme.palette.action.selected,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography sx={{ mr: 2, fontSize: '1.4rem' }}>
                {lang.flag}
              </Typography>
              <Typography>
                {lang.name}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;